import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class QsNativeCartUpsells extends Component {
  #pending = false;
  #controller;
  #needsRefresh = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    if (this.#checkMarket()) this.#syncSelections();
  }

  updatedCallback() {
    super.updatedCallback();
    if (!this.#pending) this.#needsRefresh = false;
    if (this.#checkMarket()) this.#syncSelections();
    this.#setBusy(this.#pending);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #checkMarket(currency) {
    const currentCountry = window.Shopify?.country;
    const currentCurrency = currency || window.Shopify?.currency?.active;
    if ((currentCountry && currentCountry !== this.dataset.country) ||
        (currentCurrency && currentCurrency !== this.dataset.currency)) {
      this.#needsRefresh = true;
      const list = this.querySelector('.qs-cart-upsells__list');
      if (list) list.hidden = true;
      this.#showStatus(this.dataset.currencyChanged, true);
      this.#setBusy(false);
      return false;
    }
    return !this.#needsRefresh;
  }

  #showStatus(message, refresh = false) {
    if (this.refs.status) this.refs.status.textContent = message || '';
    if (this.refs.refreshLink) this.refs.refreshLink.hidden = !refresh;
  }

  #setBusy(busy) {
    this.setAttribute('aria-busy', String(busy));
    for (const control of this.querySelectorAll('button, select')) {
      control.disabled = busy || this.#needsRefresh;
    }
  }

  #syncSelections() {
    for (const select of this.querySelectorAll('[data-qs-variant]')) {
      this.handleVariantChange({ target: select });
    }
  }

  handleVariantChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-qs-variant]')) return;
    const option = select.selectedOptions[0];
    const card = select.closest('[data-qs-product-id]');
    if (!option || !card) return;
    card.querySelector('[name="quantity"]').value = option.dataset.quantity || '1';
    card.querySelector('[data-qs-price]').textContent = option.dataset.price || '';
    const compare = card.querySelector('[data-qs-compare]');
    compare.textContent = option.dataset.compare || '';
    compare.hidden = !option.dataset.compare;
    for (const link of card.querySelectorAll('[data-qs-product-link]')) {
      const url = new URL(link.href);
      url.searchParams.set('variant', option.value);
      link.href = url.href;
    }
    const image = card.querySelector('img');
    if (image && option.dataset.image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
    }
  }

  async #fetchCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      signal: this.#controller.signal,
    });
    if (!response.ok) throw new Error(this.dataset.addError);
    return response.json();
  }

  async handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-qs-upsell-form]')) return;
    event.preventDefault();
    if (this.#pending || !this.#checkMarket() || !form.reportValidity()) return;
    const selected = form.querySelector('[data-qs-variant]')?.selectedOptions[0];
    const card = form.closest('[data-qs-product-id]');
    if (!selected || !card || selected.disabled) return;
    const variantId = selected.value;
    const quantity = Math.max(1, Number(selected.dataset.quantity) || 1);
    let deferred;
    let posted = false;
    let confirmed = false;
    this.#pending = true;
    this.#setBusy(true);
    this.#showStatus(this.dataset.adding);
    deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'cart',
      lines: [{ merchandiseId: variantId, quantity }], promise: deferred.promise,
    }));

    try {
      const cartComponent = customElements.get('cart-items-component');
      await Promise.all(cartComponent?.pendingQuantityUpdates || []);
      if (!this.isConnected) throw new DOMException('Cart recommendation disconnected', 'AbortError');
      const before = await this.#fetchCart();
      if (!this.#checkMarket(before.currency)) {
        deferred.reject(new Error(this.dataset.currencyChanged));
        return;
      }
      if (before.items.some((item) => String(item.product_id) === card.dataset.qsProductId)) {
        this.#needsRefresh = true;
        this.#showStatus(this.dataset.added, true);
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(before),
          detail: { source: 'qs-native-cart-upsells', items: before.items, didError: true },
        });
        return;
      }
      const sectionIds = [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'),
        (element) => element.dataset.sectionId))].filter(Boolean).slice(0, 5);
      if (!sectionIds.includes(this.dataset.sectionId)) {
        sectionIds.unshift(this.dataset.sectionId);
        sectionIds.splice(5);
      }
      const body = new FormData(form);
      body.set('id', variantId);
      body.set('quantity', String(quantity));
      body.set('sections', sectionIds.join(','));
      body.set('sections_url', window.location.pathname + window.location.search);
      const root = window.Shopify?.routes?.root || '/';
      posted = true;
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', body, headers: { Accept: 'application/json' },
        credentials: 'same-origin', signal: this.#controller.signal,
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        const message = typeof result.description === 'string' ? result.description : this.dataset.addError;
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        this.#showStatus(message);
        const cart = await this.#fetchCart();
        posted = false;
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { source: 'qs-native-cart-upsells', items: cart.items, didError: true },
        });
        return;
      }
      confirmed = true;
      const cart = await this.#fetchCart();
      if (!this.#checkMarket(cart.currency)) {
        deferred.reject(new Error(this.dataset.currencyChanged));
        return;
      }
      this.#showStatus(this.dataset.added);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          source: 'qs-native-cart-upsells', sourceId: this.dataset.sectionId,
          items: cart.items, sections: result.sections, didError: false,
          productId: card.dataset.qsProductId, itemCount: quantity,
        },
      });
    } catch (error) {
      deferred?.reject(error);
      if (error?.name === 'AbortError') return;
      this.#needsRefresh = posted;
      this.#showStatus(confirmed ? this.dataset.refreshError : posted ? this.dataset.pendingError : this.dataset.addError, posted);
      this.dispatchEvent(new CartErrorEvent({ error: error.message || this.dataset.addError, code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      this.#pending = false;
      this.#setBusy(false);
    }
  }
}

if (!customElements.get('qs-native-cart-upsells')) {
  customElements.define('qs-native-cart-upsells', QsNativeCartUpsells);
}
